<template>
  <div>
    <!-- Confirmation Modal -->
    <div class="modal fade" id="fixBusinessIDModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-warning">
            <h5 class="modal-title text-dark">Fix Same Business ID Confirmation</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="error" class="alert alert-danger" role="alert">
              {{ error }}
            </div>

            <div class="alert alert-warning" role="alert">
              <i class="bx bx-error-circle me-2"></i>
              <strong>Important:</strong> This action will create a new business record with a new Business ID and update the current selected record.
            </div>

            <p>You are about to fix the duplicate Business ID issue for:</p>
            <table class="table table-bordered">
              <tr>
                <th>Bill No:</th>
                <td>{{ billNo }}</td>
              </tr>
              <tr>
                <th>Business ID:</th>
                <td>{{ businessID }}</td>
              </tr>
            </table>

            <p>This will:</p>
            <ul>
              <li>Create a new unique Business ID for this record</li>
              <li>Keep all other business information unchanged</li>
              <li>Update all related records to use the new Business ID</li>
            </ul>

            <p>Are you sure you want to proceed?</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                @click="fixBusinessID"
                type="button"
                class="btn btn-warning"
                :disabled="loading"
            >
              <i class="bx bx-link-alt me-1"></i>
              Fix Business ID
              <span v-if="loading" class="spinner-border spinner-border-sm ms-1" role="status"></span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div class="modal fade" id="fixBusinessSuccessModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-success">
            <h5 class="modal-title text-white">Business ID Fixed Successfully</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="text-center mb-4">
              <div class="avatar-md mx-auto">
                <div class="avatar-title rounded-circle bg-light">
                  <i class="bx bx-check-circle text-success font-size-24"></i>
                </div>
              </div>
            </div>

            <div class="alert alert-success" role="alert">
              <i class="bx bx-check-circle me-2"></i>
              {{ resultMessage || "Business ID has been successfully fixed!" }}
            </div>

            <div class="table-responsive">
              <table class="table table-bordered">
                <tr>
                  <th width="40%">Bill No:</th>
                  <td>{{ result.billNo }}</td>
                </tr>
                <tr>
                  <th>Business Name:</th>
                  <td>{{ result.businessName }}</td>
                </tr>
                <tr>
                  <th>Old Business ID:</th>
                  <td>{{ result.originalBusinessID }}</td>
                </tr>
                <tr class="table-success">
                  <th>New Business ID:</th>
                  <td><strong>{{ result.newBusinessID }}</strong></td>
                </tr>
              </table>
            </div>

            <p class="text-muted mt-3">
              <i class="bx bx-info-circle me-1"></i>
              The business record has been updated with a new unique Business ID. All related records have been updated to reference this new ID.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="reloadPage">OK</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {execute, trade} from '@/api';

export default {
  name: 'FixSameBusinessID',
  props: {
    businessID: {
      type: String,
      required: true
    },
    billNo: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      result: {
        originalBusinessID: '',
        newBusinessID: '',
        billNo: '',
        businessName: ''
      },
      resultMessage: '',
      confirmationModal: null,
      successModal: null
    };
  },
  mounted() {
    // Initialize bootstrap modals when component is mounted
    this.setupModals();
  },
  methods: {
    setupModals() {
      // Using setTimeout to ensure DOM is fully loaded
      setTimeout(() => {
        // Initialize bootstrap modals
        this.confirmationModal = new bootstrap.Modal(document.getElementById('fixBusinessIDModal'));
        this.successModal = new bootstrap.Modal(document.getElementById('fixBusinessSuccessModal'));
      }, 100);
    },

    showModal() {
      this.error = null;

      // Ensure modals are initialized
      if (!this.confirmationModal) {
        this.setupModals();
      }

      this.confirmationModal.show();
    },

    async fixBusinessID() {
      this.loading = true;
      this.error = null;

      try {
        const data = new FormData();
        data.append("function", "fixSameBusinessID");
        data.append("billNo", this.billNo);
        data.append("businessID", this.businessID);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, trade);

        this.loading = false;

        if (response.data.success) {
          // Store the result data
          this.result = response.data.data;
          this.resultMessage = response.data.message;

          // Emit success event early
          this.$emit('success', this.result);

          // Force close the confirmation modal using both methods
          this.forceCloseConfirmationModal();

          // Show success modal after a delay
          setTimeout(() => {
            this.showSuccessModal();
          }, 500);
        } else {
          this.error = response.data.message || "Failed to fix business ID";
        }
      } catch (err) {
        this.loading = false;
        this.error = err.message || "An error occurred while fixing business ID";
      }
    },

    forceCloseConfirmationModal() {
      // Attempt to close the modal using multiple techniques to ensure it closes

      // 1. Try with stored modal instance
      if (this.confirmationModal) {
        this.confirmationModal.hide();
      }

      // 2. Try with direct DOM access
      const modalElement = document.getElementById('fixBusinessIDModal');
      if (modalElement) {
        // Remove any modal-specific classes and styles
        modalElement.classList.remove('show');
        modalElement.style.display = 'none';

        // Try to get Bootstrap instance and close it
        if (window.bootstrap) {
          const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        }

        // Remove modal backdrop if present
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.parentNode.removeChild(backdrop);
        }

        // Remove modal-open class from body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    },

    showSuccessModal() {
      // First, ensure confirmation modal is really closed
      this.forceCloseConfirmationModal();

      // Ensure the success modal is initialized
      if (!this.successModal) {
        // Try to initialize it
        const successModalElement = document.getElementById('fixBusinessSuccessModal');
        if (successModalElement && window.bootstrap) {
          this.successModal = new bootstrap.Modal(successModalElement);
        }
      }

      // Show success modal
      if (this.successModal) {
        this.successModal.show();
      } else {
        // Direct DOM approach if all else fails
        const successModalElement = document.getElementById('fixBusinessSuccessModal');
        if (successModalElement && window.bootstrap) {
          const modal = new bootstrap.Modal(successModalElement);
          modal.show();
        }
      }
    },

    reloadPage() {
      // Emit event for parent component to handle
      this.$emit('reload');
    }
  }
};
</script>